import { liveQuery } from "dexie";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import type { StockRuntimeItem } from "../runtime/gateway/local-projection-client";
import type { StockTransferRuntimeProjection } from "../runtime/gateway/stock-transfer-client";
import { readLocalEmployeeSession, type LocalEmployeeSessionState } from "../runtime/local-store/employee-session";
import {
  readStockTransferLocalItems,
  readStockTransferRuntime,
} from "../runtime/local-store/stock-transfer-runtime";
import { readTerminalCredential, type TerminalCredentialState } from "../runtime/local-store/terminal-state";
import { StockTransferWorkspace } from "./StockTransferWorkspace";

type Snapshot = {
  terminal: TerminalCredentialState | null;
  session: LocalEmployeeSessionState | null;
};

type WorkspaceState = {
  items: StockRuntimeItem[];
  initialMode: "SEND" | "RECEIVE";
};

export function StockTransferRuntimeHost() {
  const [snapshot, setSnapshot] = useState<Snapshot>({ terminal: null, session: null });
  const [runtime, setRuntime] = useState<StockTransferRuntimeProjection | null>(null);
  const [mountNode, setMountNode] = useState<Element | null>(null);
  const [workspace, setWorkspace] = useState<WorkspaceState | null>(null);
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    const subscription = liveQuery(async () => ({
      terminal: await readTerminalCredential(),
      session: await readLocalEmployeeSession(),
    })).subscribe({
      next: setSnapshot,
      error: () => setSnapshot({ terminal: null, session: null }),
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);

  useEffect(() => {
    const findMount = () => setMountNode(document.querySelector(".pos-stock-toolbar"));
    findMount();
    const observer = new MutationObserver(findMount);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const terminal = snapshot.terminal;
  const session = snapshot.session;
  const identity = terminal?.identity ?? null;
  const actor = session?.actor ?? null;
  const sameScope = Boolean(
    identity && actor
    && actor.organization_id === identity.organization_id
    && actor.branch_id === identity.branch_id
    && actor.node_id === identity.node_id
    && actor.runtime_session_status === "ACTIVE",
  );

  useEffect(() => {
    if (!identity || !sameScope) {
      setRuntime(null);
      setWorkspace(null);
      return;
    }
    const subscription = liveQuery(() => readStockTransferRuntime(identity)).subscribe({
      next: setRuntime,
      error: () => setRuntime(null),
    });
    return () => subscription.unsubscribe();
  }, [identity?.organization_id, identity?.branch_id, identity?.node_id, sameScope]);

  async function openWorkspace() {
    if (!identity || !runtime || (!runtime.can_send && !runtime.can_receive)) return;
    const items = await readStockTransferLocalItems(identity);
    items.sort((left, right) => left.canonical_name.localeCompare(right.canonical_name, "th"));
    setWorkspace({
      items,
      initialMode: runtime.can_send ? "SEND" : "RECEIVE",
    });
  }

  if (!identity || !terminal || !session?.sessionToken || !sameScope || !runtime) return null;
  const available = runtime.can_send || runtime.can_receive;
  const inboundCount = runtime.items.filter((item) => item.direction === "INCOMING" && item.status === "IN_TRANSIT" && item.next_action === "RECEIVE").length;

  return (
    <>
      {mountNode && available ? createPortal(
        <button className="pos-stock-transfer-launch" type="button" onClick={() => void openWorkspace()}>
          <strong>โอนสาขา</strong>
          <span>{inboundCount ? `รอรับ ${inboundCount.toLocaleString("th-TH")}` : runtime.can_send ? "ส่ง / รับ" : "รับเข้า"}</span>
        </button>,
        mountNode,
      ) : null}

      {workspace ? createPortal(
        <StockTransferWorkspace
          identity={identity}
          deviceSecret={terminal.deviceSecret}
          sessionToken={session.sessionToken}
          items={workspace.items}
          runtime={runtime}
          online={online}
          initialMode={workspace.initialMode}
          onClose={() => setWorkspace(null)}
        />,
        document.body,
      ) : null}
    </>
  );
}
